import { calculateLineConfigs } from "@/components/plan/roomGridHelpers";
import {
  areCoordinatesInsideArea,
  getIntersection,
} from "@/components/plan/calculations";
import { alphabet, sortString } from "@/utils/helpers";
import i18n from "../../../i18n";

export default {
  setAddingRoom: (state, value) => (state.addingRoom = value),
  setUpdatingRoom: (state, value) => (state.updatingRoom = value),
  setIsDrawing: (state, value) => (state.isDrawing = value),
  setRoomId: (state, id) => {
    state.room_id = id;
  },
  setRoomCorner: (state, corners) =>
    corners.forEach((coordinate) => state.corners.push(coordinate)),
  setIsRoomClosed: (state, value) => (state.closed = value),
  undoLastCorner: (state) => {
    state.corners = state.corners.slice(0, -2);

    if (state.closed) {
      state.closed = false;
    }
  },
  setMeasurementHeight: (state, height) => {
    state.measurement_height = height;
  },
  setRoomCorners: (state, corners) => {
    const cornersArray = [];
    corners.forEach((corner) => {
      cornersArray.push(corner.x);
      cornersArray.push(corner.y);
    });
    state.corners = cornersArray;
  },
  resetRoomCorners: (state) => {
    state.corners = [];
    state.closed = false;
    state.distance_between_points_x = 0;
    state.distance_between_points_y = 0;
    state.distance_from_wall = 0;
    state.max_distance_between_points = 0;
    state.measurement_points = [];
  },
  resetRoomState: (state) => {
    state.tab = i18n.t("room.tabs.info");
    state.isDrawing = false;
    state.updatingRoom = false;
    state.addingRoom = false;
    state.room_id = null;
    state.name = undefined;
    state.room_info_comment = undefined;
    state.room_image_url = undefined;
    state.measurement_points_comment = undefined;
    state.room_type = undefined;
    state.room_type_id = null;
    state.isRoomValid = true;
    state.longer_size_length = 1;
    state.distance_between_points_x = 0;
    state.distance_between_points_y = 0;
    state.max_distance_between_points = 0;
    state.manuallyChangingDistanceBetweenPoints = false;
    state.measurement_height = 0.75;
    state.distance_from_wall = 0;
    state.freezeDistanceBetweenPoints = false;
    state.corners = [];
    state.illumination_tests = [];
    state.functional_tests = [];
    state.measurement_points = [];
    state.measurement_points_comment = undefined;
    state.selected_test_id = null;
    state.selected_measurement_point_id = null;
    state.closed = false;
  },
  setRoomName: (state, name) => {
    state.name = name;
  },
  setMeasurementPointsComment: (state, comment) => {
    state.measurement_points_comment = comment;
  },
  setRoomInfoComment: (state, comment) => {
    state.room_info_comment = comment;
  },
  setRoomImageUrl: (state, room_image_url) => {
    state.room_image_url = room_image_url;
  },
  setIlluminationTests: (state, tests) => {
    state.illumination_tests = tests;
  },
  setFunctionalTests: (state, tests) => {
    state.functional_tests = tests;
  },
  addIlluminationTest: (state, test) => {
    state.illumination_tests.push(test);
  },
  addFunctionalTest: (state, test) => {
    state.functional_tests.push(test);
  },
  changeIlluminationTestAttribute: (state, changed) => {
    const { index, attribute, newValue } = changed;
    const currentTest = state.illumination_tests[index];
    const newTest = { ...currentTest, [attribute]: newValue };

    state.illumination_tests.splice(index, 1, newTest);
  },
  changeFunctionalTestAttribute: (state, changed) => {
    const { index, attribute, newValue } = changed;
    const currentTest = state.functional_tests[index];
    const newTest = { ...currentTest, [attribute]: newValue };

    state.functional_tests.splice(index, 1, newTest);
  },
  deleteIlluminationTest: (state, index) => {
    state.illumination_tests.splice(index, 1);
  },
  deleteFunctionalTest: (state, index) => {
    state.functional_tests.splice(index, 1);
  },
  setRoomUse: (state, use) => {
    state.room_type = use;
  },
  setRoomType: (state, room_type_id) => {
    state.room_type_id = room_type_id;
    const selectedRoomType = state.room_types.find(
      (room_type) => room_type.room_type_id === room_type_id
    );
    if (selectedRoomType) {
      state.room_type = `${selectedRoomType.type_id} ${selectedRoomType.room_type}`;
    }
  },
  setRoomLength: (state, length) => {
    state.longer_size_length = length;
  },
  setIsRoomValid: (state, value) => {
    state.isRoomValid = value;
  },
  setDistanceBetweenPointsX: (state, value) => {
    state.distance_between_points_x = value;
  },
  setDistanceBetweenPointsY: (state, value) => {
    state.distance_between_points_y = value;
  },
  setDistanceFromWall: (state, distance) => {
    state.distance_from_wall = distance;
  },
  setMaxDistanceBetweenPoints: (state, value) => {
    state.max_distance_between_points = value;
  },
  setFreezeDistanceBetweenPoints: (state, value) => {
    state.freezeDistanceBetweenPoints = value;
  },
  setMeasurementPoints: (state, value) => {
    state.measurement_points = value.sort(sortString);
  },
  setMeasurementPointsByCalculation: (state, getInsideRoomCorners) => {
    if (!state.corners || !state.longer_size_length || !state.closed) {
      return;
    }

    const { horizontalLineConfigs, verticalLineConfigs } = calculateLineConfigs(
      state.corners,
      state.longer_size_length
    );

    const horizontalLines = horizontalLineConfigs.map((config) =>
      config.points.map((point) => +point.toFixed(2))
    );
    const verticalLines = verticalLineConfigs.map((config) =>
      config.points.map((point) => +point.toFixed(2))
    );

    const points = [];

    const insideRoomCorners = [];
    getInsideRoomCorners.forEach((coordinate, i) => {
      if (i % 2 === 0) {
        insideRoomCorners.push([
          getInsideRoomCorners[i],
          getInsideRoomCorners[i + 1],
        ]);
      }
    });

    horizontalLines.forEach((horizontalLine, index) => {
      verticalLines.forEach((verticalLine, i) => {
        const intersection = getIntersection(
          ...horizontalLine,
          ...verticalLine
        );

        if (
          intersection &&
          areCoordinatesInsideArea(intersection, insideRoomCorners)
        ) {
          points.push({
            x: intersection.x,
            y: intersection.y,
            location: `${alphabet(i + 1)} ${index + 1}`,
            is_active: true,
          });
        } else if (
          intersection &&
          !areCoordinatesInsideArea(intersection, insideRoomCorners)
        ) {
          points.push({
            x: intersection.x,
            y: intersection.y,
            location: `${alphabet(i + 1)} ${index + 1}`,
            is_active: false,
          });
        }
      });
    });
    state.measurement_points = points;
  },
  activateDeactivateAllPoints: (state) => {
    const allPointsInactive = state.measurement_points.every(
      (point) => !point.is_active
    );
    state.measurement_points.forEach((point, i) => {
      state.measurement_points[i].is_active = allPointsInactive;
    });
  },
  activateDeactivatePoint: (state, id) => {
    state.measurement_points.find((point) => point.location === id).is_active =
      !state.measurement_points.find((point) => point.location === id)
        .is_active;
  },
  setTab: (state, value) => {
    state.tab = value;
  },

  // all these below are for the installer view

  setSelectedTestId: (state, value) => {
    state.selected_test_id = value;
  },
  setSelectedMeasurementPointId: (state, value) => {
    state.selected_measurement_point_id = value;
  },
  setMeasurementConditionsForIlluminationTest: (
    state,
    { illumination_test_id, measurement_conditions }
  ) => {
    const index = state.illumination_tests.findIndex(
      (test) => test.illumination_test_id === illumination_test_id
    );
    if (index !== -1) {
      state.illumination_tests[index].measurement_conditions =
        measurement_conditions;
    }
  },
  setResultForIlluminationTest: (
    state,
    { illumination_test_id, allResults, measurement_point_id }
  ) => {
    const result = {
      ...allResults.find(
        (result) => result.measurement_point_id === measurement_point_id
      ),
    };
    const testIndex = state.illumination_tests.findIndex(
      (test) => test.illumination_test_id === illumination_test_id
    );
    const resultIndex = state.illumination_tests[
      testIndex
    ].illumination_test_results.findIndex(
      (result) => result.measurement_point_id === measurement_point_id
    );
    if (resultIndex === -1) {
      state.illumination_tests[testIndex].illumination_test_results.push(
        result
      );
    } else {
      state.illumination_tests[testIndex].illumination_test_results[
        resultIndex
      ] = result;
    }
  },
  updateResultForIlluminationTest: (
    state,
    { illumination_test_id, illumination_test_result_id, updatedResult }
  ) => {
    const testIndex = state.illumination_tests.findIndex(
      (test) => test.illumination_test_id === illumination_test_id
    );
    const resultIndex = state.illumination_tests[
      testIndex
    ].illumination_test_results.findIndex(
      (result) =>
        result.illumination_test_result_id === illumination_test_result_id
    );
    state.illumination_tests[testIndex].illumination_test_results[resultIndex] =
      updatedResult;
  },
  setFunctionalTestResult: (state, { functional_test_id, result }) => {
    const testIndex = state.functional_tests.findIndex(
      (test) => test.functional_test_id === functional_test_id
    );
    const resultIndex = state.functional_tests[
      testIndex
    ].functional_test_results.findIndex(
      (res) => res.question_identifier === result.question_identifier
    );

    if (resultIndex === -1) {
      state.functional_tests[testIndex].functional_test_results.push({
        ...result,
      });
    } else {
      state.functional_tests[testIndex].functional_test_results.splice(
        resultIndex,
        1,
        { ...result }
      );
    }
  },
  setActualNumberOfZones: (
    state,
    { functional_test_id, savedNumberOfZones }
  ) => {
    const index = state.functional_tests.findIndex(
      (test) => test.functional_test_id === functional_test_id
    );
    state.functional_tests[index].actual_number_of_zones = savedNumberOfZones;
  },
  setTestCompliance: (
    state,
    { test_id, test_type, is_compliant, compliance_comment }
  ) => {
    if (test_type === "illumination") {
      const testIndex = state.illumination_tests.findIndex(
        ({ illumination_test_id }) => illumination_test_id === test_id
      );
      const updatedTests = [...state.illumination_tests];

      updatedTests[testIndex] = {
        ...state.illumination_tests[testIndex],
        is_compliant,
        compliance_comment,
      };

      state.illumination_tests = updatedTests;
    }

    if (test_type === "functional") {
      const testIndex = state.functional_tests.findIndex(
        ({ functional_test_id }) => functional_test_id === test_id
      );
      const updatedTests = [...state.functional_tests];

      updatedTests[testIndex] = {
        ...state.functional_tests[testIndex],
        is_compliant,
        compliance_comment,
      };

      state.functional_tests = updatedTests;
    }
  },
  setComplianceComment: (state, { id, compliance_comment }) => {
    if (
      state.illumination_tests.find((test) => test.illumination_test_id === id)
    ) {
      state.illumination_tests.find(
        (test) => test.illumination_test_id === id
      ).compliance_comment = compliance_comment;
    } else {
      state.functional_tests.find(
        (test) => test.functional_test_id === id
      ).compliance_comment = compliance_comment;
    }
  },
  setRoomTypes: (state, room_types) => {
    state.room_types = room_types;
  },
};
